import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { CheckSquare, Eye, FileText, Inbox, List, Plus, Palette, Circle } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../core/auth/AuthContext';
import { CreateListeModal } from './CreateListeModal';
import { useTaskColorMode } from '../hooks/useTaskColorMode';
import { useTranslation } from 'react-i18next';

interface Liste {
  id: string;
  nom: string;
  couleur: string | null;
  est_defaut: boolean;
  total_taches: number;
  taches_terminees: number;
}

export function ZilloTasksSidebar() {
  const { t } = useTranslation('zillotasks');
  const { account } = useAuth();
  const location = useLocation();
  const [listes, setListes] = useState<Liste[]>([]);
  const [sidebarCounts, setSidebarCounts] = useState<{ my: number; general: number; documents: number; suivis: number }>({ my: 0, general: 0, documents: 0, suivis: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { isMulticolor, toggle: toggleColorMode } = useTaskColorMode();

  const loadListes = () => {
    api.getZtListes().then((res) => {
      if (res.success && res.data) {
        setListes(Array.isArray(res.data) ? res.data : []);
      }
    }).catch(() => {});
  };

  const loadCounts = () => {
    api.getZtSidebarCounts().then((res) => {
      if (res.success && res.data) {
          const d = res.data;
          setSidebarCounts({ my: d.my, general: d.general, documents: d.documents, suivis: d.suivis ?? 0 });
        }
    }).catch(() => {});
  };

  useEffect(() => {
    loadListes();
    loadCounts();
  }, []);

  // Refresh counts on route change or when tasks are mutated
  useEffect(() => {
    loadCounts();
    loadListes();
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => { loadCounts(); loadListes(); };
    window.addEventListener('zt-tasks-changed', handler);
    return () => window.removeEventListener('zt-tasks-changed', handler);
  }, []);

  const customListes = listes.filter((l) => !l.est_defaut);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
      isActive ? 'bg-slate-300 dark:bg-white/15 text-gray-900 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300 hover:bg-slate-300/70 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
    }`;

  return (
    <aside className="w-60 h-full bg-slate-200 dark:bg-gray-800 text-gray-700 dark:text-white flex-shrink-0 flex flex-col overflow-hidden border-r border-gray-300 dark:border-gray-700">
      {/* Module Header */}
      <div
        className="px-4 py-4 border-b border-gray-300 dark:border-gray-700 flex-shrink-0"
        style={{ borderLeftColor: '#7c3aed', borderLeftWidth: '3px' }}
      >
        <h2 className="font-semibold">ZilloTasks</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('sidebar.taskManagement')}</p>
      </div>

      {/* Navigation */}
      <nav className="p-2 flex-shrink-0">
        <div className="mb-2">
          <h3 className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {t('sidebar.tasks')}
          </h3>
          <ul className="space-y-1">
            <li>
              <NavLink to="/zillotasks" end className={navLinkClass}>
                <CheckSquare size={18} />
                <span className="flex-1">{t('myTasks.title')}</span>
                {sidebarCounts.my > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-gray-200 dark:bg-white/15 text-gray-600 dark:text-gray-300 text-xs font-medium">
                    {sidebarCounts.my}
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/zillotasks/general" className={navLinkClass}>
                <Inbox size={18} />
                <span className="flex-1">{t('general.title')}</span>
                {sidebarCounts.general > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-gray-200 dark:bg-white/15 text-gray-600 dark:text-gray-300 text-xs font-medium">
                    {sidebarCounts.general}
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/zillotasks/documents" className={navLinkClass}>
                <FileText size={18} />
                <span className="flex-1">{t('sidebar.documents')}</span>
                {sidebarCounts.documents > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-gray-200 dark:bg-white/15 text-gray-600 dark:text-gray-300 text-xs font-medium">
                    {sidebarCounts.documents}
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/zillotasks/suivis" className={navLinkClass}>
                <Eye size={18} />
                <span className="flex-1">{t('sidebar.suivis')}</span>
                {sidebarCounts.suivis > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-gray-200 dark:bg-white/15 text-gray-600 dark:text-gray-300 text-xs font-medium">
                    {sidebarCounts.suivis}
                  </span>
                )}
              </NavLink>
            </li>
          </ul>
        </div>
      </nav>

      <div className="border-t border-gray-300 dark:border-gray-700 flex-shrink-0" />

      {/* Custom lists */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="px-4 pt-3 pb-1 flex items-center justify-between flex-shrink-0">
          <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {t('sidebar.lists')}
          </h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-white hover:bg-slate-300/70 dark:hover:bg-white/10 rounded transition-colors"
            title={t('sidebar.newList')}
          >
            <Plus size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-2 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full">
          {customListes.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 italic">{t('sidebar.noCustomLists')}</p>
          ) : (
            customListes.map((liste) => {
              const pendingCount = liste.total_taches - liste.taches_terminees;
              return (
                <NavLink
                  key={liste.id}
                  to={`/zillotasks/liste/${liste.id}`}
                  className={navLinkClass}
                >
                  {liste.couleur ? (
                    <div
                      className="w-4 h-4 rounded flex-shrink-0"
                      style={{ backgroundColor: liste.couleur }}
                    />
                  ) : (
                    <List size={18} className="flex-shrink-0" />
                  )}
                  <span className="flex-1 truncate">{liste.nom}</span>
                  {pendingCount > 0 && (
                    <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-gray-200 dark:bg-white/15 text-gray-600 dark:text-gray-300 text-xs font-medium">
                      {pendingCount}
                    </span>
                  )}
                </NavLink>
              );
            })
          )}
        </div>
      </div>

      {/* Footer — color mode toggle + account name */}
      <div className="flex-shrink-0 px-3 py-2.5 border-t border-gray-300 dark:border-gray-700 space-y-2">
        <button
          onClick={toggleColorMode}
          className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg text-xs transition-colors hover:bg-slate-300/70 dark:hover:bg-white/10"
          title={isMulticolor ? t('sidebar.switchToMonochrome') : t('sidebar.switchToMulticolor')}
        >
          {isMulticolor ? (
            <Palette size={15} className="text-amber-300 flex-shrink-0" />
          ) : (
            <Circle size={15} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
          )}
          <span className={isMulticolor ? 'text-gray-700 dark:text-white/90' : 'text-gray-500 dark:text-gray-400'}>
            {isMulticolor ? t('sidebar.multicolor') : t('sidebar.monochrome')}
          </span>
          <span className={`ml-auto w-7 h-4 rounded-full flex items-center transition-colors ${isMulticolor ? 'bg-amber-400/80 justify-end' : 'bg-gray-300 dark:bg-white/20 justify-start'}`}>
            <span className={`w-3 h-3 rounded-full mx-0.5 transition-colors ${isMulticolor ? 'bg-white' : 'bg-gray-400 dark:bg-white/50'}`} />
          </span>
        </button>
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center truncate">{account?.name}</p>
      </div>

      {/* Create Liste Modal */}
      {showCreateModal && (
        <CreateListeModal
          onClose={() => setShowCreateModal(false)}
          onCreated={loadListes}
        />
      )}
    </aside>
  );
}

export default ZilloTasksSidebar;
